export { useDeleteMessage } from './message-delete-queries';
export type { DeleteMessageResult } from './message-delete-contract';
import { agentQueries } from '@/features/settings/agent-queries';
import { customerPanelQueries } from './customer-panel-queries';
export { useUpdateNote, useDeleteNote } from './customer-panel-queries';
export type { BillingCustomer, BillingInvoice, ConversationBillingResult } from './customer-panel-queries';
export { useMessageEdits, useToggleMessageReaction, useSendToBts } from './message-menu-queries';
import {conversationContextQueries} from './conversation-context-queries';
export { useSendMessage, useSendMessagesBatch, useCreateNote } from './message-write-queries';
export type { SendMessageArgs, SendMessageBatchItem, BatchSendResult, BatchSendResponse } from './message-write-contract';
export { useResolveConversation, useReopenConversation, useMarkConversationUnread, useMuteConversation, useUpdateConversation, useSendCsat, useRequestContactInfo } from './conversation-action-queries';
export { useAssignConversation, useAssignConversationToTeam } from './conversation-assignment-queries';
import { LocalizedError } from '@/lib/i18n/errors';
import {
  infiniteQueryOptions,
  useMutation,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { client as sdkClient } from '@blazeteams/sdk/client';
import {
  canLoadOlder,
  isInvalidCursorError,
  mergeOlderMessages,
  readMessagesMeta,
  type MessagesMeta,
} from './message-pagination';
import {
  MAX_HISTORY_PAGES,
  MESSAGES_PAGE_SIZE,
  THREAD_HISTORY_INICIAL,
  useMessageHistoryStore,
} from './message-history-store';
import {
  getInboxConversations,
  getInboxConversationsByIdMessages,
  postInboxConversationsByIdRead,
  postInboxConversationsBulkTag,
  postInboxContactsMerge,
  getInboxStats,
  getInboxContactsById,
  getInboxContactsByIdStats,
  putInboxContactsById,
  getTeams,
} from '@blazeteams/sdk';
import type {
  ConversationResponse,
  InboxMessage,
  ConversationStatsResponse,
  ContactResponse,
  UpdateContactRequest,
} from '@blazeteams/sdk';
import { permissionQueries } from '@/features/auth/permission-queries';
import { useAuthStore } from '@/store/auth-store';
import { useInboxPresenceStore } from './inbox-presence-store';
import { normalizeBulkResult } from './bulk-result';
import { normalizeTagFilters, type TagMatchMode } from './tag-filter';
import { normalizeInboxMessage } from './message-media';
import { patchConversationContactCache } from './contact-cache';
import { conversationDateBoundary } from './conversation-filter-dates';
import { conversationListFailure, readConversationList } from './conversation-list-contract';
import type { ConversationListRow } from './conversation-event-preview';
import { requireCurrentSession, sessionRevision, sessionTenantId } from '@/features/auth/session-scope';

/**
 * One reaction entry on a message. Backend persists these in the message's
 * metadata.reactions array (or a sibling `reactions` field — both shapes are
 * observed at runtime), and pushes WS events `inbox:message_reaction` with
 * `{message_id, emoji, user_id, user_name, action: 'added'|'removed'}` when
 * an agent reacts.
 */
export interface MessageReaction {
  emoji: string;
  user_id?: string;
  user_name?: string;
  /** Some backends include the timestamp; not relied on by the UI */
  ts?: number;
}

/**
 * The regenerated SDK exposes the inbox message as `InboxMessage`. The
 * backend still emits a `user_name` field on outbound messages (the agent
 * display name) which the spec doesn't declare, plus reactions and a
 * `reply_to` snapshot when the message is a reply — extend the type locally
 * so we can read them without `as any`. Drop these once the spec catches up.
 */
export type Message = InboxMessage & {
  /** Optional native generated failure copy; validated against failed_reason.
   * Authored metadata never supplies this projection. */
  failure_presentation?: unknown;
  /** Checked server-owned default-template body origin. */
  template_presentation?: unknown;
  user_name?: string;
  reactions?: MessageReaction[];
  /** Snapshot of the parent message when this is a reply. Backend may inflate
   *  it inline OR only send `reply_to_id`; read defensively. */
  reply_to?: { id?: string; content?: string; user_name?: string; direction?: string; template_presentation?: unknown };
  /** Caption that travels alongside an image/video/document. Backend deploy
   *  b9354b7 added this field; older messages have it on `content` instead,
   *  so consumers fall back via `media_caption ?? content`. */
  media_caption?: string;
  /** Lifecycle for provider-hosted inbound attachments. While `pending`,
   *  `media_url` is a provider media id rather than a fetchable URL. */
  media_status?: 'ready' | 'pending' | 'failed';
  /** Byte size supplied by `inbox:message_media_ready` when known. */
  media_size?: number;
};

/**
 * `is_muted` is on the backend domain (InboxConversation, persisted via
 * migration 028) and the realtime payload, but the swagger annotation for
 * the inbox list/detail response hasn't been refreshed yet — so it doesn't
 * appear on the generated `ConversationResponse`. Treat it as an optional
 * field via a small wrapper type and prefer this where mute state matters.
 * Drop once the response DTO catches up.
 */
export type Conversation = ConversationListRow & {
  is_muted?: boolean;
  /** Backend-computed attention triage (Smart Assistance / "Ashley"). These ride
   *  on the conversation list response but aren't in the SDK yet — same forward
   *  pattern as `is_muted`. See docs/superpowers/specs/2026-06-08-smart-assistance-attention-design.md */
  awaiting_reply?: boolean;
  /** RFC3339 timestamp of the oldest customer message not yet answered by a HUMAN
   *  agent (bot replies don't count); null/absent when not awaiting. */
  waiting_since?: string | null;
  /** Composite urgency score (waiting_min × priority_w × vip_w), for sorting. */
  attention_score?: number;
  /** RFC3339 close time of the Meta messaging window (24h CSW / 72h FEP for
   *  WhatsApp; last_inbound+24h for Messenger). Backend tracks it on the domain
   *  (window_expires_at) but the response DTO/SDK don't expose it yet — same
   *  forward pattern as `is_muted`. Absent → window feature dormant. */
  window_expires_at?: string | null;
  /** Origin of the open window: "service" | "marketing" | "referral_conversion" | … */
  window_origin_type?: string | null;
  /** Snooze ("Posponer") — additive DTO fields, backend Matrix #94376.
   *  RFC3339 wake-up time; ABSENT/null when not snoozed OR snoozed
   *  indefinitely ("hasta que responda") — do NOT derive snoozed state
   *  from this field. */
  snoozed_until?: string | null;
  /** Agent who snoozed the conversation (additive #94376). */
  snoozed_by?: string;
  /** COMPUTED server-side: the conversation is currently "parada". THE
   *  source of truth for snooze state/badges (additive #94376). */
  is_snoozed?: boolean;
  /** Canal 1 de coordinación de la lista (additive, spec 2026-07-09):
   *  `nueva|esperando_agente|esperando_cliente|al_dia`, SIEMPRE presente
   *  post-deploy. Tipado `string` (no union) a propósito: el server puede
   *  emitir valores futuros que este build no conoce — `coordinationStateOf`
   *  en `coordination.ts` los ignora y re-deriva client-side. */
  coordination_state?: string;
  /** Canal 2: snapshot de viewers vivos al momento del fetch (omitempty —
   *  AUSENTE si nadie la está viendo). Efímero, viene del hub WS, no de DB.
   *  La verdad en vivo la llevan los eventos `inbox:agent_viewing`/
   *  `inbox:agent_left_viewing` vía el inbox-presence-store; este snapshot
   *  solo lo SIEMBRA en cada fetch. */
  presence?: {
    viewers: Array<{ user_id: string; name: string; avatar?: string }>;
    viewer_count: number;
  };
};

/**
 * Context bubble payload — AI summary + addon panels surfaced as a
 * floating "fuap" above the conversation thread. Backend regenerates
 * the `ai_summary` after every N inbound messages (configurable via
 * AI_CONTEXT_REFRESH_THRESHOLD env var, default 5) or on demand via
 * POST /context/refresh.
 *
 * `ai_stale` is computed server-side from the message count delta —
 * the UI uses it to nudge the agent to refresh when 5+ new messages
 * landed since the last summarisation.
 */
export type {ConversationContext,AddonPanel,AddonItem} from './conversation-context-keys';
export {useRefreshConversationContext} from './conversation-context-queries';
/**
 * Pull reactions out of a message regardless of whether the backend put them
 * on a top-level `reactions` field or under `metadata.reactions`. Returns an
 * empty array if neither exists.
 */
export function getMessageReactions(msg: Message): MessageReaction[] {
  if (Array.isArray(msg.reactions)) return msg.reactions;
  const fromMeta = (msg.metadata as { reactions?: MessageReaction[] } | undefined)?.reactions;
  if (Array.isArray(fromMeta)) return fromMeta;
  return [];
}

// Backend wraps list responses in { data: [...] } even though the OpenAPI spec
// declares a bare array. Unwrap defensively until SDK and backend agree.
function unwrapList<T>(payload: unknown, key?: string): T[] {
  if (Array.isArray(payload)) return payload as T[];
  if (payload && typeof payload === 'object') {
    const obj = payload as Record<string, unknown>;
    // Top-level keyed list: `{ agents: [...] }`
    if (key && Array.isArray(obj[key])) return obj[key] as T[];
    // Top-level data array: `{ data: [...] }`
    if (Array.isArray(obj.data)) return obj.data as T[];
    // Verbose envelope: `{ data: { agents: [...], total }, success }` — backend
    // wraps list endpoints inside the standard `{ data, success }` envelope
    // even when the list itself is keyed (agents, conversations, …).
    if (obj.data && typeof obj.data === 'object') {
      const inner = obj.data as Record<string, unknown>;
      if (key && Array.isArray(inner[key])) return inner[key] as T[];
      if (Array.isArray(inner.data)) return inner.data as T[];
    }
  }
  return [];
}

function unwrap<T>(payload: unknown): T {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as { data: T }).data;
  }
  return payload as T;
}

function errMsg(error: unknown, fallback: string): string {
  return (error as { message?: string })?.message ?? fallback;
}

/**
 * Siembra el snapshot de presencia (Canal 2 de coordinación) desde
 * `ConversationResponse.presence` como efecto lateral del fetch — mismo
 * patrón que `agents()` hidratando el presence store global. El snapshot
 * REEMPLAZA el set de esa conversación (server autoritativo al momento del
 * fetch); los eventos `inbox:agent_viewing`/`agent_left_viewing` lo mantienen
 * vivo entre fetches. `presence` ausente = nadie mirando → limpia la entrada.
 */
export function seedPresenceSnapshots(convs: Conversation[]): void {
  const selfId = useAuthStore.getState().user?.id;
  const store = useInboxPresenceStore.getState();
  for (const conv of convs) {
    if (!conv.id) continue;
    store.seedSnapshot(
      conv.id,
      conv.presence?.viewers?.map((v) => ({
        userId: v.user_id,
        name: v.name,
        avatar: v.avatar,
      })),
      selfId,
    );
  }
}

/**
 * Single source of truth for how an inbox tab maps to (a) the server `status` +
 * any server-side narrowing param, and (b) the client-side predicate the server
 * does NOT enforce. Both the plain `conversations` query and the paginated
 * `conversationsInfinite` query consume this so the visibility-critical
 * `team`/`unassigned` semantics (see the module doc above + AGENTS.md) can never
 * drift between the two. The backend already role-scopes the list, so the client
 * predicate only ever *narrows* — it must never widen.
 */
function planConversationQuery(args: ConversationsFilterArgs): {
  /** Server `status` param. `undefined` → OMIT it entirely (the `snoozed`
   *  tab queries by `snoozed=true` alone; empty keys are never sent as ""). */
  status?: string;
  serverExtra?: Record<string, string | number | boolean>;
  clientFilter: (c: ConversationResponse) => boolean;
} {
  // "Pospuestas" is a pure server-side query (`?snoozed=true`, ordered by
  // snoozed_until asc BY THE SERVER — don't re-sort) with NO status param
  // and NO client-side predicate. The active tabs already EXCLUDE snoozed
  // rows server-side (#94376) — never re-filter them here (P0 rule).
  const hasSearch = !!args.search?.trim();
  const status =
    args.filter === 'snoozed'
      ? undefined
      : args.filter === 'resolved'
        ? 'resolved'
        : hasSearch
          ? undefined
          : 'open,pending';

  // "Mías" narrows server-side via assigned_user_id (falls back to all-active if
  // we somehow don't know the current user). "Pospuestas" narrows server-side
  // via snoozed=true.
  const serverExtra: Record<string, string | number | boolean> | undefined =
    args.filter === 'snoozed'
      ? { snoozed: true }
      : args.filter === 'mine' && args.currentUserId
        ? { assigned_user_id: args.currentUserId }
        : undefined;

  const teamIds = args.teamIds ?? [];
  const fromMs = conversationDateBoundary(args.dateFrom)?.getTime() ?? null;
  const toMs = conversationDateBoundary(args.dateTo, true)?.getTime() ?? null;

  const clientFilter = (c: ConversationResponse): boolean => {
    // Assignment-based tab narrowing the server doesn't apply.
    if (args.filter === 'team') {
      if (!c.assigned_team_id || !teamIds.includes(c.assigned_team_id)) return false;
    } else if (args.filter === 'unassigned') {
      if (c.assigned_user_id || c.assigned_team_id) return false;
    }
    // Inclusive date range over last_message_at (end-of-day on `to`).
    if (fromMs !== null || toMs !== null) {
      if (!c.last_message_at) return false;
      const ms = new Date(c.last_message_at).getTime();
      if (!Number.isFinite(ms)) return false;
      if (fromMs !== null && ms < fromMs) return false;
      if (toMs !== null && ms > toMs) return false;
    }
    return true;
  };

  return { status, serverExtra, clientFilter };
}

/** Page size for the paginated conversation list (infinite scroll). */
const CONVERSATIONS_PAGE_SIZE = 50;

/** One page of the paginated conversation list. */
export interface ConversationsPage {
  /** Rows after the client-side predicate (what the UI renders). */
  items: ConversationResponse[];
  /** Raw rows the server returned for this page, BEFORE client filtering —
   *  pagination math keys off this, not `items.length`. */
  rawCount: number;
  /** Server-side total for the status query (null when the backend omitted meta). */
  total: number | null;
  /** Offset to request for the next page (`offset + rawCount`). */
  nextOffset: number;
}

/**
 * Inbox queue tabs. Mostly an ASSIGNMENT taxonomy (who is responsible) layered
 * over the active work set (`status` open|pending) — distinct from
 * `contact.status` (the CRM follow-up state).
 *
 *  - `all`        → active work: open + pending merged (no resolved/closed)
 *  - `mine`       → active, assigned to the current agent
 *  - `team`       → active, assigned to one of the agent's teams
 *                   (`assigned_team_id ∈ myTeamIds`). This is the queue a bot
 *                   routes into; agents MUST be able to find these.
 *  - `unassigned` → active, NOBODY assigned (both `assigned_user_id` and
 *                   `assigned_team_id` absent). Since the 2026-07-22 strict
 *                   assignment contract, this queue is only exposed in the UI
 *                   to roles with `customer_chat_view_all`.
 *  - `resolved`   → status=resolved
 *  - `snoozed`    → `?snoozed=true`, NO status param (#94376). Fully
 *                   server-side: the server orders by snoozed_until asc and
 *                   the active tabs already exclude snoozed rows — no
 *                   client-side predicate or re-sort here.
 *
 * `team` and the view_all-only `unassigned` queue are filtered client-side over
 * the already-scoped list; `mine` narrows server-side via `assigned_user_id`.
 */
export type InboxFilter = 'all' | 'mine' | 'team' | 'unassigned' | 'resolved' | 'snoozed';

export interface ConversationsFilterArgs {
  filter: InboxFilter;
  /** Global historical search. While present, the implicit active-work
   *  `status=open,pending` is omitted so resolved/closed matches remain
   *  discoverable. An explicitly selected lifecycle view may still set its
   *  own status (currently `resolved`). */
  search?: string;
  currentUserId?: string;
  /** The current agent's team UUIDs — required for the `team` filter. Sourced
   *  from the permissions store; passed in so it participates in the queryKey. */
  teamIds?: string[];
  /** When set, only return conversations belonging to this channel. */
  channelId?: string;
  /** Server-side priority filter (low, normal, high, urgent). */
  priority?: string;
  /** Server-side unread predicate (`is_unread=true`). */
  isUnread?: boolean;
  /** Associated customer has no global labels. Sent server-side as
   * `contact_untagged=true`, independently from conversation `untagged`. */
  untagged?: boolean;
  /** Legacy single conversation-tag alias; prefer conversationTags. */
  tag?: string;
  /** Server-side tags on the conversation itself. */
  conversationTags?: string[];
  /** Server-side tags on the associated customer contact. */
  contactTags?: string[];
  /** `any` matches at least one selected tag; `all` requires every tag. */
  tagMatch?: TagMatchMode;
  /** Sort field — e.g. 'newest', 'oldest', 'priority', 'unread'. */
  sort?: string;
  /** Inclusive lower bound on `last_message_at` (ISO date YYYY-MM-DD or full
   *  timestamp). Sent as the backend's `date_from` query parameter. */
  dateFrom?: string;
  /** Inclusive upper bound on `last_message_at`. End-of-day semantics —
   *  a value of "2026-04-30" includes anything with last_message_at on
   *  that calendar day. */
  dateTo?: string;
}

/**
 * Builds the wire query shared by the snapshot and infinite conversation
 * queries. Keeping this pure makes it difficult for the two list surfaces to
 * drift (especially for `is_unread`, dates and sort values).
 */
export function buildConversationsQuery(
  args: ConversationsFilterArgs,
  pagination: { limit: number; offset?: number },
): Record<string, string | number | boolean | string[]> {
  const { status, serverExtra } = planConversationQuery(args);
  const { conversationTags, contactTags, tagMatch } = normalizeTagFilters(args);
  const query: Record<string, string | number | boolean | string[]> = {
    limit: pagination.limit,
    ...(pagination.offset === undefined ? {} : { offset: pagination.offset }),
    ...(status ? { status } : {}),
  };
  const search = args.search?.trim();
  if (search) query.search = search;
  if (args.channelId) query.channel_id = args.channelId;
  if (args.priority) query.priority = args.priority;
  if (args.isUnread) query.is_unread = true;
  if (args.untagged) query.contact_untagged = true;
  // Pospuestas: el server ordena por snoozed_until asc (contrato #94376)
  // — el sort global del toolbar no aplica y no debe pisarlo.
  if (args.sort && args.sort !== 'newest' && args.filter !== 'snoozed') {
    query.sort = args.sort;
  }
  if (args.dateFrom) query.date_from = args.dateFrom;
  if (args.dateTo) query.date_to = args.dateTo;
  if (serverExtra) Object.assign(query, serverExtra);
  if (conversationTags.length) query.tags = conversationTags;
  if (contactTags.length) query.contact_tags = contactTags;
  if (tagMatch === 'all') query.tag_match = tagMatch;
  return query;
}

/**
 * Shape of `GET /me/permissions`. The endpoint isn't in the generated SDK yet,
 * so we hit it through the shared client. Backend envelope: `{ success, data }`.
 * `permissions` is the full RolePermissions map; we mirror
 * `customer_chat_view_all` (visibility) and `customer_chat_forward` (the
 * "Reenviar" action) into the permissions store, and keep the bag for future
 * gates.
 */
export type { MyPermissions } from '@/features/auth/permission-queries';

/**
 * El backend devuelve `400 {error:"invalid_cursor"}` ante un `before` corrupto,
 * inexistente o de otra conversación — nunca la primera página. Se conserva el
 * código en un error propio porque `errMsg()` se queda con el `message` (que
 * viene en castellano) y perdería la única señal accionable.
 *
 * La reacción correcta a esto NO es reintentar sin cursor: eso devolvería lo
 * que el cliente ya tenía, en un loop que desde afuera se ve como "no carga
 * más" — el síntoma exacto que la paginación viene a matar.
 */
export class InvalidCursorError extends LocalizedError {
  readonly code = 'invalid_cursor';
  constructor() {
    super('inbox', 'history.changed');
    this.name = 'InvalidCursorError';
  }
}

/**
 * Una página del hilo. `before` ausente = la ventana más nueva.
 *
 * El SDK tipa `query` como `{ limit?, offset? }`: el swagger de este endpoint
 * todavía no declara `before` (pedido en comms #191500), así que hay que
 * castear a través de `unknown` — mismo patrón que `ChannelConfig`. En cuanto
 * el swagger lo declare y se regenere el SDK, el cast se cae solo.
 */
async function fetchMessagesPage(
  conversationId: string,
  params: { limit: number; before?: string | null; signal?: AbortSignal },
): Promise<{ rows: Message[]; meta: MessagesMeta }> {
  const query: Record<string, unknown> = { limit: params.limit };
  if (params.before) query.before = params.before;

  const { data, error } = await getInboxConversationsByIdMessages({
    path: { id: conversationId },
    query: query as unknown as { limit?: number; offset?: number },
    signal: params.signal,
  });

  if (error) {
    if (isInvalidCursorError(error)) {
      throw new InvalidCursorError();
    }
    throw new LocalizedError('inbox', 'history.readFailed');
  }

  return {
    rows: unwrapList<unknown>(data, 'messages').map(
      (message) => normalizeInboxMessage(message) as Message,
    ),
    meta: readMessagesMeta(data),
  };
}

/**
 * Reconstruye la ventana del hilo que el agente estaba mirando: recorre desde
 * la página más nueva hacia atrás tantas páginas como diga el store.
 *
 * POR QUÉ SE RECORRE DE NUEVO EN VEZ DE GUARDAR LOS CURSORES
 * ---------------------------------------------------------
 * Cada recorrido deriva el cursor de la página N+1 del `meta` de la página N
 * RECIÉN traída. Eso es lo que lo hace inmune a una conversación viva: si
 * entraron `k` mensajes desde el último fetch, la ventana entera se corre `k`
 * lugares de forma coherente, sin huecos ni repetidos. Un cursor guardado de
 * un fetch anterior, en cambio, envejece — y ahí es donde aparecen los
 * agujeros en el medio del hilo.
 *
 * (Es el mismo algoritmo que usa TanStack para refetchear un infinite query:
 * la página 0 se pide sin cursor y las siguientes recalculan su parámetro
 * sobre el resultado fresco. Acá se hace a mano para que el cache siga siendo
 * un `Message[]` plano — ver `message-history-store.ts` para el porqué.)
 *
 * Un fallo en la PRIMERA página es un fallo del hilo y se propaga. Un fallo en
 * una página vieja devuelve la ventana parcial y deja el error a la vista: es
 * mejor mostrar 100 mensajes y avisar que no se pudo traer más, que borrar el
 * hilo entero porque falló el scroll hacia arriba.
 */
async function walkMessageHistory(
  conversationId: string,
  ctx: { client?: QueryClient; signal?: AbortSignal } = {},
): Promise<Message[]> {
  const { client, signal } = ctx;
  const cacheKey = ['messages', conversationId] as const;
  // Foto al arranque: sólo para decidir si HAY algo que preservar ante un
  // fallo. La cola se relee al final (ver el cierre de la función).
  const alArrancar = client?.getQueryData<Message[]>(cacheKey);
  const yaHabiaHilo = Array.isArray(alArrancar) && alArrancar.length > 0;

  const solicitadas = Math.min(
    Math.max(1, useMessageHistoryStore.getState().get(conversationId).pages),
    MAX_HISTORY_PAGES,
  );

  let thread: Message[] = [];
  let meta: MessagesMeta = THREAD_HISTORY_INICIAL.meta;
  let cursor: string | null = null;
  let recorridas = 0;
  let fallo = false;

  for (let pagina = 0; pagina < solicitadas; pagina += 1) {
    let resultado: { rows: Message[]; meta: MessagesMeta };
    try {
      resultado = await fetchMessagesPage(conversationId, {
        limit: MESSAGES_PAGE_SIZE,
        before: cursor,
        signal,
      });
    } catch (err) {
      // Un fallo en cualquier página no puede devolver una ventana MÁS CHICA
      // que la que el agente ya está viendo: eso le borraría de la pantalla
      // mensajes que sí tenía. Si ya hay hilo cargado se relanza, porque React
      // Query CONSERVA el `data` anterior ante un error y sólo marca `isError`
      // — el hilo queda intacto y el aviso sale por la franja de arriba.
      // Sólo en la primera carga (sin nada que conservar) tiene sentido
      // devolver lo parcial.
      if (pagina === 0 || yaHabiaHilo) {
        if (pagina > 0) {
          useMessageHistoryStore
            .getState()
            .setError(conversationId, err instanceof LocalizedError ? err : new LocalizedError('inbox', 'history.olderFailed'));
        }
        throw err;
      }
      fallo = true;
      useMessageHistoryStore
        .getState()
        .setError(conversationId, err instanceof LocalizedError ? err : new LocalizedError('inbox', 'history.olderFailed'));
      break;
    }

    thread = mergeOlderMessages(resultado.rows, thread);
    meta = resultado.meta;
    recorridas = pagina + 1;

    if (!canLoadOlder(meta)) break;
    cursor = meta.nextCursor;
  }

  // Un walk abortado (TanStack cancela el anterior en cada refetch) no escribe
  // nada en el store: su resultado se descarta, y si igual hidratara, el walk
  // que gana el cache y el que gana el store podrían ser distintos.
  if (!signal?.aborted) {
    const store = useMessageHistoryStore.getState();
    store.hydrate(conversationId, meta, recorridas);
    if (!fallo && recorridas > 0) store.setError(conversationId, null);
  }

  // La página 0 se pide al PRINCIPIO del recorrido, así que para cuando el
  // walk termina puede tener varios segundos de antigüedad. Lo que haya
  // llegado por WS mientras tanto ya está en el cache (upsert quirúrgico) y su
  // handler renunció al invalidate de respaldo justo porque el upsert
  // funcionó: devolver `thread` pelado lo borraría de la pantalla sin que nada
  // lo traiga de vuelta. Por eso el cache se relee ACÁ, al final — leerlo al
  // arranque no vería nada de lo que llegó durante el recorrido, que es
  // exactamente lo que hay que rescatar.
  return reconciliarVentana(thread, client?.getQueryData<Message[]>(cacheKey));
}

function instante(message: Message): number {
  const ts = new Date(message.created_at ?? 0).getTime();
  return Number.isNaN(ts) ? 0 : ts;
}

/**
 * Reconcilia la ventana que trajo el server con la que el agente ya tenía.
 *
 * El server es autoritativo DENTRO de su ventana: lo que cae entre el mensaje
 * más viejo y el más nuevo del recorrido y no vino, no se conserva. Los dos
 * extremos son otra cosa:
 *
 * - **Cola** (más nueva que el recorrido): lo que llegó por WS mientras el
 *   walk traía historia vieja, más los envíos optimistas sin confirmar. El
 *   handler de `inbox:new_message` renuncia a su `invalidate` de respaldo
 *   cuando el upsert funciona, así que descartarlos acá los borraría de la
 *   pantalla sin que nada los traiga de vuelta.
 *
 * - **Cabeza** (más vieja que el recorrido): la historia que el agente ya
 *   había scrolleado. Hace falta porque la ventana está anclada en la CABEZA
 *   del hilo: si entran 50 mensajes en vivo, "2 páginas desde el más nuevo" ya
 *   no llega hasta donde el agente había cargado, y sin esto la historia se le
 *   evapora del scroll. Se conserva **sólo si el mensaje más viejo del
 *   recorrido está en el cache**: esa coincidencia PRUEBA que las dos ventanas
 *   se solapan y por lo tanto que la unión es contigua. Sin la prueba se
 *   descarta, porque prependear a ciegas crearía justo el agujero invisible
 *   que toda esta paginación viene a evitar.
 */
function reconciliarVentana(thread: Message[], cacheado: Message[] | undefined): Message[] {
  if (!Array.isArray(cacheado) || cacheado.length === 0 || thread.length === 0) return thread;

  const enElRecorrido = new Set(thread.map((m) => m.id).filter(Boolean) as string[]);
  const masViejo = instante(thread[0]);
  const masNuevo = instante(thread[thread.length - 1]);
  const haySolapamiento = !!thread[0].id && cacheado.some((m) => m.id === thread[0].id);

  const cabeza: Message[] = [];
  const cola: Message[] = [];
  for (const message of cacheado) {
    if (message.id && enElRecorrido.has(message.id)) continue;
    const ts = instante(message);
    // Las dos ramas son disjuntas incluso con masViejo === masNuevo.
    if (ts < masViejo) {
      if (haySolapamiento) cabeza.push(message);
    } else if (ts >= masNuevo) {
      cola.push(message);
    }
  }

  if (cabeza.length === 0 && cola.length === 0) return thread;
  return [...cabeza, ...thread, ...cola];
}

/**
 * "Cargar mensajes anteriores": trae UNA página más vieja y la prependea.
 *
 * Es incremental a propósito — un solo request, no un recorrido entero — así
 * que el costo del click no crece con lo que el agente ya scrolleó. El
 * recorrido completo queda para los refetch (WS, invalidación), que es donde
 * hace falta la coherencia de toda la ventana.
 *
 * El techo de la ventana se sube ANTES de pedir: si un refetch por WS entra
 * mientras este fetch está en vuelo, reproduce la ventana grande y no pisa el
 * bloque recién prependeado con una más chica. Si el pedido falla, se
 * devuelve el techo a donde estaba.
 */
export function useLoadOlderMessages(conversationId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationKey: ['messages', conversationId, 'older'],
    mutationFn: async () => {
      const store = useMessageHistoryStore.getState();
      const previo = store.get(conversationId);

      // Sin ancla no hay pedido posible. `canLoadOlder` exige has_more Y
      // cursor: ofrecer el botón sin poder cumplirlo es peor que no ofrecerlo.
      if (!canLoadOlder(previo.meta) || previo.loading) return;

      if (previo.pages >= MAX_HISTORY_PAGES) {
        store.setError(
          conversationId,
          new LocalizedError('inbox', 'history.capReached', {count:MAX_HISTORY_PAGES * MESSAGES_PAGE_SIZE}),
        );
        return;
      }

      store.setLoading(conversationId, true);
      store.setError(conversationId, null);
      const objetivo = store.wantMore(conversationId);

      try {
        const { rows, meta } = await fetchMessagesPage(conversationId, {
          limit: MESSAGES_PAGE_SIZE,
          before: previo.meta.nextCursor,
        });

        // Sin esto, un walk que estaba en vuelo resuelve DESPUÉS y reemplaza
        // el cache con su propia ventana —armada con un `pages` viejo—,
        // deshaciendo el click del agente. Es el mismo motivo por el que un
        // update optimista cancela primero.
        await qc.cancelQueries({ queryKey: ['messages', conversationId] });

        qc.setQueryData<Message[]>(['messages', conversationId], (prev) =>
          Array.isArray(prev) ? mergeOlderMessages(rows, prev) : prev,
        );
        // El techo puede haber subido más por otro camino mientras tanto: no
        // bajarlo nunca acá.
        const actual = useMessageHistoryStore.getState();
        actual.hydrate(
          conversationId,
          meta,
          Math.max(objetivo, actual.get(conversationId).pages),
        );
      } catch (err) {
        // Rollback RELATIVO: sólo se deshace el propio `wantMore`, y sólo si
        // nadie más movió el techo mientras tanto — un rollback absoluto
        // dejaría la ventana más chica que el cache.
        const actual = useMessageHistoryStore.getState();
        actual.rollbackPages(conversationId, objetivo);
        actual.setError(
          conversationId,
          err instanceof LocalizedError ? err : new LocalizedError('inbox', 'history.olderFailed'),
        );
      } finally {
        // `loading` es de esta mutación: `hydrate` ya no lo toca, así que un
        // refetch de fondo que termina en el medio no le apaga el spinner.
        useMessageHistoryStore.getState().setLoading(conversationId, false);
      }
    },
  });
}

export const inboxQueries = {
  // Non-paginated single-shot list (capped at 100). Kept for widgets that only
  // need a snapshot (e.g. the dashboard). The inbox list itself uses
  // `conversationsInfinite` below so older conversations stay reachable.
  conversations: (args: ConversationsFilterArgs = { filter: 'all' }) => {
    const revision = sessionRevision(), tenant = sessionTenantId(useAuthStore.getState());
    return {
    queryKey: [
      'conversations',
      args.filter,
      args.search?.trim() || null,
      args.currentUserId ?? null,
      (args.teamIds ?? []).join(',') || null,
      args.channelId ?? null,
      args.priority ?? null,
      args.isUnread ?? false,
      args.untagged ?? false,
      normalizeTagFilters(args).conversationTags.join(',') || null,
      normalizeTagFilters(args).contactTags.join(',') || null,
      normalizeTagFilters(args).tagMatch,
      args.sort ?? null,
      args.dateFrom ?? null,
      args.dateTo ?? null,
    ],
    queryFn: async ({ signal }: { signal?: AbortSignal } = {}) => {
      requireCurrentSession(revision);
      const { clientFilter } = planConversationQuery(args);
      const query = buildConversationsQuery(args, { limit: 100 });

      const { data, error, response } = await getInboxConversations({ query, signal });
      requireCurrentSession(revision);
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      if (error) throw conversationListFailure(error, response?.status);
      const { rows: raw } = readConversationList(data, tenant);
      // Seed ANTES del clientFilter: una conv narroweada fuera de este tab
      // igual trae snapshot fresco que otras vistas consumen.
      seedPresenceSnapshots(raw);
      return raw.filter(clientFilter);
    },
    };
  },

  /**
   * Paginated conversation list for infinite scroll. Pages by `offset` and stops
   * when `offset + rawCount >= meta.total` (or, if the backend omits `meta`, when
   * a page comes back short). The realtime manager invalidates `['conversations']`,
   * which prefix-matches this key too — TanStack then refetches every LOADED page,
   * so new WS events keep the scrolled-in history fresh instead of breaking it.
   *
   * Client-side narrowing (`team`/`unassigned`/`date`) is applied per page,
   * so pagination math keys off `rawCount` (server rows), never `items.length`.
   */
  conversationsInfinite: (args: ConversationsFilterArgs = { filter: 'all' }) => {
    const revision = sessionRevision(), tenant = sessionTenantId(useAuthStore.getState());
    return infiniteQueryOptions({
      queryKey: [
        'conversations',
        'infinite',
        args.filter,
        args.search?.trim() || null,
        args.currentUserId ?? null,
        (args.teamIds ?? []).join(',') || null,
        args.channelId ?? null,
        args.priority ?? null,
        args.isUnread ?? false,
        args.untagged ?? false,
        normalizeTagFilters(args).conversationTags.join(',') || null,
        normalizeTagFilters(args).contactTags.join(',') || null,
        normalizeTagFilters(args).tagMatch,
        args.sort ?? null,
        args.dateFrom ?? null,
        args.dateTo ?? null,
      ],
      initialPageParam: 0,
      queryFn: async ({ pageParam, signal }): Promise<ConversationsPage> => {
        requireCurrentSession(revision);
        const offset = pageParam;
        const { clientFilter } = planConversationQuery(args);
        const query = buildConversationsQuery(args, {
          limit: CONVERSATIONS_PAGE_SIZE,
          offset,
        });

        const { data, error, response } = await getInboxConversations({ query, signal });
        requireCurrentSession(revision);
        if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
        if (error) throw conversationListFailure(error, response?.status);
        const { rows: raw, total } = readConversationList(data, tenant);
        seedPresenceSnapshots(raw);
        return {
          items: raw.filter(clientFilter),
          rawCount: raw.length,
          total,
          nextOffset: offset + raw.length,
        };
      },
      getNextPageParam: (last) => {
        // Defensive: never advance on an empty page (avoids an infinite loop if
        // the backend ever returns rawCount 0 with a stale total).
        if (last.rawCount === 0) return undefined;
        if (last.total != null) {
          return last.nextOffset < last.total ? last.nextOffset : undefined;
        }
        // No meta.total → keep going only while pages arrive full.
        return last.rawCount >= CONVERSATIONS_PAGE_SIZE ? last.nextOffset : undefined;
      },
    });
  },

  /**
   * Hilo de mensajes. El cache es —y tiene que seguir siendo— un `Message[]`
   * plano en orden ASC: hay ~15 escritores quirúrgicos sobre esta clave que
   * guardan con `Array.isArray(prev) ? … : prev` y no-opearían en silencio
   * ante cualquier otra forma. La paginación vive en `message-history-store`.
   */
  messages: (conversationId: string) => ({
    queryKey: ['messages', conversationId],
    queryFn: ({ client, signal }: { client: QueryClient; signal: AbortSignal }) =>
      walkMessageHistory(conversationId, { client, signal }),
    enabled: !!conversationId,
  }),

  stats: () => ({
    queryKey: ['inbox', 'stats'],
    queryFn: async () => {
      const { data, error } = await getInboxStats();
      if (error) throw new Error(errMsg(error, 'Failed to load stats'));
      // Backend wraps this in `{ success, data: { total_open, … } }`. Reading
      // the envelope as the stats object left every field undefined → the
      // dashboard showed "0 conversaciones" and "—" everywhere. Unwrap it.
      return unwrap<ConversationStatsResponse>(data);
    },
  }),

  notes: customerPanelQueries.notes,

  context: conversationContextQueries.context,

  contact: (contactId: string | undefined) => ({
    queryKey: ['contact', contactId],
    queryFn: async () => {
      if (!contactId) throw new Error('contactId required');
      const { data, error } = await getInboxContactsById({ path: { id: contactId } });
      if (error) throw new Error(errMsg(error, 'Failed to load contact'));
      return unwrap<ContactResponse>(data);
    },
    enabled: !!contactId,
  }),

  contactStats: (contactId: string | undefined) => ({
    queryKey: ['contact', contactId, 'stats'],
    queryFn: async () => {
      if (!contactId) throw new Error('contactId required');
      const { data, error } = await getInboxContactsByIdStats({ path: { id: contactId } });
      if (error) throw new Error(errMsg(error, 'Failed to load contact stats'));
      // Backend (inbox_service.go ContactStats) returns:
      //   { conversations_count: int, messages_count: int64,
      //     last_activity: time.Time | null }
      // Wrapped in { success, data }. Earlier I had guessed
      // total_conversations/open_conversations/etc. — those don't exist and
      // every Stat box just rendered 0.
      return unwrap<{
        conversations_count?: number;
        messages_count?: number;
        last_activity?: string;
      }>(data);
    },
    enabled: !!contactId,
  }),

  contactConversations: customerPanelQueries.history,

  agents: agentQueries.list,

  teams: () => ({
    queryKey: ['teams'],
    queryFn: async () => {
      const { data, error } = await getTeams();
      if (error) throw new Error(errMsg(error, 'Failed to load teams'));
      // Backend may wrap as { data: [...] } / { teams: [...] } or return a
      // bare array — the SDK type is `{ [key: string]: unknown }` so we have
      // to probe defensively. unwrapList handles all three shapes.
      return unwrapList<{
        id?: string;
        name?: string;
        description?: string;
        avatar_url?: string;
        member_count?: number;
      }>(data, 'teams');
    },
  }),

  /**
   * The current agent's customer-chat visibility scope. Drives the "Mi equipo"
   * tab and the realtime upsert's visibility check. As a side-effect (matching
   * `agents()` hydrating the presence store), it mirrors the result into the
   * permissions store so non-React code (the realtime manager) can read it
   * synchronously. Endpoint not in the SDK → shared client.
   */
  myPermissions: permissionQueries.current,

  conversationBilling: customerPanelQueries.billing,
};

export function useMarkConversationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (conversationId: string) => {
      const { data, error } = await postInboxConversationsByIdRead({
        path: { id: conversationId },
      });
      if (error) throw new Error(errMsg(error, 'Failed to mark as read'));
      return data;
    },
    // Optimistically zero the unread badge in every cached conversations list
    // (any filter variant) so the badge disappears the moment the user opens
    // the chat — without waiting for the HTTP roundtrip or the WS echo.
    onMutate: async (conversationId) => {
      await qc.cancelQueries({ queryKey: ['conversations'] });
      qc.setQueriesData<ConversationResponse[]>(
        { queryKey: ['conversations'] },
        (prev) => {
          if (!Array.isArray(prev)) return prev;
          return prev.map((c) => (c.id === conversationId ? { ...c, unread_count: 0 } : c));
        },
      );
      qc.setQueryData<ConversationResponse>(
        ['conversation', conversationId],
        (prev) => (prev ? { ...prev, unread_count: 0 } : prev),
      );
    },
    onSuccess: () => {
      // Recalculate the global unread total in the inbox sidebar
      qc.invalidateQueries({ queryKey: ['inbox', 'stats'] });
    },
  });
}

// ===== Notes =====

/**
 * Force a fresh AI summary regeneration on demand. Synchronous on the
 * backend (~2-5s while the Summarizer responds), so the UI shows a
 * spinner during the call. Backend also broadcasts `inbox:context_updated`
 * to all observers, but the mutation's optimistic write here lands the
 * fresh payload in the agent's own cache instantly.
 */
/**
 * On-demand AI reply suggestion for a conversation (backend Matrix #91508):
 * `POST /inbox/conversations/:id/suggest-reply` → `{ suggestion: string }`.
 * Synchronous LLM call (same ReplySuggester as the auto-suggest, but explicit
 * — no "last message must be inbound" gate). CONTRACT GOTCHA: `suggestion`
 * can legitimately be `""` (AI off for the tenant / no provider / quota /
 * empty thread) — that is NOT an error; consumers must show a friendly
 * "no suggestion available" state. 404 = no access (inbox visibility model),
 * 502 = the AI provider failed.
 */
export { useSuggestReply } from './composer-ai-queries';

// ===== Contact =====

export function useUpdateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ contactId, body }: { contactId: string; body: UpdateContactRequest }) => {
      const { data, error } = await putInboxContactsById({
        path: { id: contactId },
        body,
      });
      if (error) throw new Error(errMsg(error, 'Failed to update contact'));
      return unwrap<ContactResponse>(data);
    },
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: ['contact', vars.contactId], exact: true });
      const previousContact = qc.getQueryData<ContactResponse>(['contact', vars.contactId]);
      const previousConversationCaches = qc.getQueriesData({ queryKey: ['conversations'] });

      // The active DetailsTab observes this exact key. Merge the delta before
      // the request leaves so selecting/removing a tag never waits on network
      // latency (the server response and invalidation reconcile afterwards).
      if (previousContact) {
        qc.setQueryData<ContactResponse>(['contact', vars.contactId], {
          ...previousContact,
          ...vars.body,
        });
      }
      qc.setQueriesData<unknown>({ queryKey: ['conversations'] }, (cache: unknown) =>
        patchConversationContactCache(cache, vars.contactId, vars.body),
      );
      return { previousContact, previousConversationCaches };
    },
    onError: (_error, vars, context) => {
      if (context?.previousContact) {
        qc.setQueryData(['contact', vars.contactId], context.previousContact);
      }
      for (const [queryKey, cache] of context?.previousConversationCaches ?? []) {
        qc.setQueryData(queryKey, cache);
      }
    },
    onSuccess: (updated, vars) => {
      // Optimistic write of the full updated contact — same instant-
      // visibility motivation as the notes/messages mutations. Without
      // it, an agent renaming a contact watches the field flicker
      // between old and new while the refetch flies. The mutation
      // already returned the canonical server-side object, so writing
      // it directly to cache costs nothing and removes the gap.
      if (updated?.id) {
        qc.setQueryData<ContactResponse>(['contact', vars.contactId], updated);
        qc.setQueriesData<unknown>({ queryKey: ['conversations'] }, (cache: unknown) =>
          patchConversationContactCache(cache, vars.contactId, updated),
        );
        // Patch any contact-list variants that include this row so
        // ContactsPage / ContactList reflect the change without
        // waiting for refetch. setQueriesData with a partial key
        // matches every filter combination cached.
        qc.setQueriesData<ContactResponse[]>(
          { queryKey: ['contacts'] },
          (prev) => {
            if (!Array.isArray(prev)) return prev;
            return prev.map((c) => (c.id === vars.contactId ? updated : c));
          },
        );
      }
      qc.invalidateQueries({ queryKey: ['contact', vars.contactId] });
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}

// ===== Conversation update (tags, priority, subject, team) =====



// ===== Reactions =====

// ===== Bulk actions =====

// SDK types declare `body?: never` for bulk endpoints but the backend expects
// a JSON body with conversation_ids. We cast through `unknown` to pass the body.

type BulkResp = { data?: unknown; error?: unknown };





export function useBulkTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ conversationIds, tags }: { conversationIds: string[]; tags: string[] }) => {
      const res = (await postInboxConversationsBulkTag({
        body: { conversation_ids: conversationIds, tags },
      } as unknown as Parameters<typeof postInboxConversationsBulkTag>[0])) as BulkResp;
      if (res.error) throw new Error(errMsg(res.error, 'Failed to bulk tag'));
      return normalizeBulkResult(res.data, conversationIds.length);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['conversations'] }),
  });
}

/**
 * Add tags to ONE conversation WITHOUT clobbering its existing tags.
 *
 * Routes through the bulk-tag endpoint on purpose: that handler MERGES (set
 * union) the incoming tags with whatever is currently on the row in the DB,
 * whereas `PUT /inbox/conversations/:id` REPLACES the whole `tags` array. The
 * async AI auto-analyzer writes `sentiment:`/`ai:`/`intent:` tags straight to
 * the row a few seconds after an inbound message; a full-array PUT built from a
 * slightly stale client snapshot would silently drop those — the
 * "los tags AI no persisten" bug. A server-side union is immune to that race.
 *
 * Optimistically unions the tags into the detail cache so the chip shows
 * instantly, then invalidates detail + list so the server reconciles.
 */
export function useAddConversationTags() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ conversationId, tags }: { conversationId: string; tags: string[] }) => {
      const res = (await postInboxConversationsBulkTag({
        body: { conversation_ids: [conversationId], tags },
      } as unknown as Parameters<typeof postInboxConversationsBulkTag>[0])) as BulkResp;
      if (res.error) throw new Error(errMsg(res.error, 'Failed to add tags'));
      return normalizeBulkResult(res.data, 1);
    },
    onMutate: async ({ conversationId, tags }) => {
      await qc.cancelQueries({ queryKey: ['conversation', conversationId] });
      const prev = qc.getQueryData<ConversationResponse>(['conversation', conversationId]);
      if (prev) {
        const merged = Array.from(new Set([...(prev.tags ?? []), ...tags]));
        qc.setQueryData<ConversationResponse>(['conversation', conversationId], {
          ...prev,
          tags: merged,
        });
      }
      return { prev };
    },
    onError: (_e, vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(['conversation', vars.conversationId], ctx.prev);
    },
    onSettled: (_d, _e, vars) => {
      qc.invalidateQueries({ queryKey: ['conversation', vars.conversationId] });
      qc.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}



// ===== CSAT =====



// ===== Contact merge =====

export function useMergeContacts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ primaryId, secondaryIds }: { primaryId: string; secondaryIds: string[] }) => {
      const { data, error } = await postInboxContactsMerge({
        body: { primary_contact_id: primaryId, secondary_ids: secondaryIds },
      });
      if (error) throw new Error(errMsg(error, 'Failed to merge contacts'));
      return data as { merged_count?: number; conversations_moved?: number; primary_contact?: ContactResponse };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['conversations'] });
      qc.invalidateQueries({ queryKey: ['contact'] });
    },
  });
}

// ===== Message edit history =====

/**
 * Manually (re)transcribe a voice note. The backend endpoint isn't in the
 * generated SDK yet, so we hit it through the shared `sdkClient` to keep
 * auth + base-url interceptors (same pattern as the context endpoints).
 *
 *   POST /inbox/conversations/{id}/messages/{messageId}/transcribe
 *
 * Synchronous + idempotent server-side: a message already `completed` returns
 * the cached transcript instantly (no provider cost); otherwise it transcribes
 * and returns the updated MessageResponse. A `failed` outcome comes back as a
 * 200 with `audio_transcript_status: 'failed'` (a valid state, not an HTTP
 * error). 503 means the backend has no transcription provider configured.
 *
 * On success we merge the returned message into the messages cache so the
 * bubble flips to the new state (transcript / failed) in the same render —
 * the WS `inbox:audio_transcript` echo reconciles the same fields if it
 * arrives too.
 */
export function useTranscribeMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      conversationId,
      messageId,
    }: {
      conversationId: string;
      messageId: string;
    }) => {
      const { data, error, response } = await sdkClient.post<{ data?: Message } | Message>({
        url: '/inbox/conversations/{id}/messages/{messageId}/transcribe',
        path: { id: conversationId, messageId },
      });
      if (error) {
        if (response?.status === 503) {
          throw new Error('Transcripción no disponible — el servidor no tiene un proveedor configurado.');
        }
        throw new Error(errMsg(error, 'No se pudo transcribir el audio'));
      }
      return unwrap<Message>(data);
    },
    onSuccess: (updated, vars) => {
      if (!updated?.id) return;
      qc.setQueryData<Message[]>(['messages', vars.conversationId], (prev) => {
        if (!Array.isArray(prev)) return prev;
        return prev.map((m) => (m.id === updated.id ? { ...m, ...updated } : m));
      });
    },
  });
}

/**
 * Send a customer's payment receipt (a message, typically an image) to BTS
 * through BlazeConnector for async validation:
 *
 *   POST /inbox/conversations/{id}/send-to-bts   body: { message_id }
 *   → 202 { status: 'processing' }
 *
 * The 202 only means "queued" — the real outcome arrives later over WS as
 * `inbox:bts_result` (handled in realtime-manager.ts, stored in `useBtsStore`).
 * On accept we mark the message as sent in the BTS store so the menu action
 * disables immediately. Endpoint not in the generated SDK → shared client.
 * Gate the UI on `useModuleEnabled('send_to_bts')`.
 */
/**
 * Edit an already-sent OUTBOUND text message. The endpoint isn't in the SDK
 * yet, so we hit it through the shared client (same pattern as transcribe):
 *
 *   PUT /inbox/conversations/{id}/messages/{messageId}  body: { content }
 *
 * Editability depends on the channel provider — Telegram and webchat can edit
 * a delivered message; WhatsApp/Messenger/Instagram/Email cannot (a Meta/SMTP
 * limitation). The UI gates the action by provider, but the backend is the
 * source of truth and answers 422 when it won't edit:
 *   - 422 { error: 'edit_not_supported' } → the channel's provider can't edit
 *   - 422 { error: 'not_editable' }        → not outbound / not text / deleted
 *   - 404                                  → message not found
 * We surface the backend's error code as the thrown Error's message so the
 * edit box can map it to a friendly line.
 */
export { useEditMessage } from './message-edit-queries';

export { useForwardMessage, useForwardMessageToContact } from './message-forward-queries';
export type { ForwardMessageArgs, ForwardMessageResult, ForwardMessageToContactArgs, ForwardToContactResult } from './message-forward-queries';

/**
 * Send a reaction on a customer-chat message. Endpoint:
 *   POST /inbox/conversations/{id}/actions/reaction  body: {emoji, message_id}
 *
 * Backend semantics (from the handler doc):
 * - Each POST creates a new `message_type: 'reaction'` message with
 *   metadata `{message_id: <target external_id>, emoji}`.
 * - Per the docstring, posting `emoji: ""` removes the reaction — but the
 *   request validation currently rejects empty emoji. Until backend fixes
 *   that, "removing" a reaction means re-clicking which actually adds a
 *   second reaction message. The UI dedupes by (user_id, emoji) so it looks
 *   correct even with duplicates in the table.
 * - Real-time: backend broadcasts `inbox:new_message` for the reaction
 *   message AND a separate `inbox:message_reaction` event. We rely on the
 *   former to refetch, so we don't need an optimistic update here.
 */
/**
 * Walk a message list and split it into:
 *   - `messages`: regular messages to render in the chat
 *   - `reactionsByTarget`: keyed by the target message's external_id, the
 *     list of reaction emojis users have applied
 * Reaction-type messages don't render as their own bubbles; they're
 * surfaced as chips below the target message. Backend stores them as
 * regular messages with metadata.message_id = target external_id and
 * content/metadata.emoji = the emoji.
 */
export function partitionReactions(messages: Message[]): {
  messages: Message[];
  reactionsByTarget: Map<string, MessageReaction[]>;
} {
  const reactionsByTarget = new Map<string, MessageReaction[]>();
  const regular: Message[] = [];
  for (const m of messages) {
    if (m.message_type === 'reaction') {
      const meta = m.metadata as { message_id?: string; emoji?: string } | undefined;
      const targetExtId = meta?.message_id;
      const emoji = meta?.emoji ?? m.content;
      if (targetExtId && emoji) {
        const existing = reactionsByTarget.get(targetExtId) ?? [];
        existing.push({
          emoji,
          user_id: m.user_id,
          user_name: m.user_name,
        });
        reactionsByTarget.set(targetExtId, existing);
      }
      // skip the reaction message — don't render it as its own bubble
      continue;
    }
    regular.push(m);
  }
  return { messages: regular, reactionsByTarget };
}
